import { useEffect, useState } from "react";
import { fetchStats, fetchAttention, fetchListings, fetchAnalytics } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Gem, BadgeDollarSign, TrendingUp, Boxes, AlertCircle, Wrench, Tag, LineChart, Clock, PiggyBank } from "lucide-react";
import { listingAgeLabel, isStaleListing } from "@/lib/listing";

const fmtNum = (n) => new Intl.NumberFormat("en-US").format(n || 0);
const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

function StatCard({ label, value, sub, icon: Icon, accent = false, testid }) {
  return (
    <div
      className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-6 flex flex-col gap-5 hover:border-[#D4AF37]/30 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">{label}</span>
        <Icon size={14} className={accent ? "text-[#3A2A10]" : "text-[#3A2A10]"} />
      </div>
      <div className={`font-mono-data text-3xl ${accent ? "text-[#3A2A10]" : "text-[#0A0604]"}`}>{value}</div>
      {sub && <div className="text-xs text-[#3A2A10]">{sub}</div>}
    </div>
  );
}

function Bar({ label, count, max, testid }) {
  const pct = max ? (count / max) * 100 : 0;
  return (
    <div className="flex items-center gap-4 py-2" data-testid={testid}>
      <div className="w-36 text-sm text-[#0A0604] truncate">{label}</div>
      <div className="flex-1 h-[2px] bg-[#2B2214]/[0.14] relative">
        <div
          className="absolute left-0 top-0 h-full bg-[#D4AF37]/70"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="font-mono-data text-sm text-[#3A2A10] w-12 text-right">{fmtNum(count)}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [attention, setAttention] = useState(null);
  const [listings, setListings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchStats(), fetchAttention(), fetchListings(), fetchAnalytics()])
      .then(([s, a, l, an]) => { setStats(s); setAttention(a); setListings(l); setAnalytics(an); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-5 sm:px-8 md:px-12 py-10 pb-28 max-w-[1600px]">
      <header className="flex items-end justify-between mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-2">Collection Overview</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0A0604] leading-none">
            The Vault
          </h1>
        </div>
        <Link
          to="/inventory"
          className="group flex items-center gap-2 text-sm text-[#3A2A10] hover:text-[#E5C158] transition-colors"
          data-testid="dashboard-view-inventory"
        >
          View Inventory
          <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </Link>
      </header>

      {loading && (
        <div className="text-sm text-[#3A2A10]" data-testid="dashboard-loading">Loading…</div>
      )}

      {stats && (
        <>
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            <StatCard
              label="Total Watches"
              value={fmtNum(stats.total)}
              sub={`${fmtNum(stats.unsold)} active · ${fmtNum(stats.sold)} sold`}
              icon={Boxes}
              accent
              testid="stat-total"
            />
            <StatCard
              label="Capital Invested"
              value={fmtMoney(stats.money.total_all_in)}
              sub={`Purchase: ${fmtMoney(stats.money.total_purchase)}`}
              icon={BadgeDollarSign}
              testid="stat-invested"
            />
            <StatCard
              label="Sales Earnings"
              value={fmtMoney(stats.money.total_earnings)}
              sub={`Gross sales: ${fmtMoney(stats.money.total_sale)}`}
              icon={Gem}
              testid="stat-earnings"
            />
            <StatCard
              label="Profit / Loss"
              value={fmtMoney(stats.money.total_profit)}
              sub={stats.money.total_profit >= 0 ? "In the black" : "Below cost basis"}
              icon={TrendingUp}
              testid="stat-profit"
            />
          </section>

          {/* Watches Needing Attention */}
          <AttentionPanel attention={attention} />

          {/* Currently for Sale */}
          <ListingsPanel listings={listings} />

          {/* Analytics — ROI by brand, time-to-sale, acquisition trend */}
          <AnalyticsPanel analytics={analytics} />

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-10">
            <Panel title="By Status" testid="panel-status">
              {stats.by_status.slice(0, 10).map((s) => (
                <Bar
                  key={s.label}
                  label={s.label}
                  count={s.count}
                  max={Math.max(...stats.by_status.map((x) => x.count))}
                  testid={`status-bar-${s.label}`}
                />
              ))}
            </Panel>
            <Panel title="Top Brands" testid="panel-brands">
              {stats.by_brand.map((s) => (
                <Bar
                  key={s.label}
                  label={s.label}
                  count={s.count}
                  max={Math.max(...stats.by_brand.map((x) => x.count))}
                  testid={`brand-bar-${s.label}`}
                />
              ))}
            </Panel>
            <Panel title="Movement Type" testid="panel-movement">
              {stats.by_movement.map((s) => (
                <Bar
                  key={s.label}
                  label={s.label}
                  count={s.count}
                  max={Math.max(...stats.by_movement.map((x) => x.count))}
                  testid={`movement-bar-${s.label}`}
                />
              ))}
            </Panel>
          </section>
        </>
      )}
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif-display text-xl text-[#0A0604]">{title}</h2>
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Distribution</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function AttentionRow({ row }) {
  const nav = useNavigate();
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => nav(`/inventory/${row.watch.id}`)}
      onKeyDown={(e) => { if (e.key === "Enter") nav(`/inventory/${row.watch.id}`); }}
      className="flex flex-wrap items-center gap-3 sm:gap-5 px-4 sm:px-5 py-3.5 hover:bg-[#2B2214]/[0.06] transition-colors group cursor-pointer"
      data-testid={`attention-row-${row.watch.id}`}
    >
      <span className="font-mono-data text-[#3A2A10] text-sm min-w-[48px]">#{row.watch.excel_id ?? "—"}</span>
      <div className="min-w-0 flex-1 basis-[60%] sm:basis-auto">
        <div className="text-sm text-[#0A0604] group-hover:text-[#3A2A10] transition-colors truncate">
          {row.watch.brand || "Unbranded"}
          <span className="text-[#3A2A10]"> · </span>
          <span className="font-mono-data text-[#3A2A10]">{row.watch.caliber || "—"}</span>
        </div>
        <div className="text-[11px] text-[#3A2A10] mt-0.5 truncate">
          {row.watch.model || "—"}
          {row.watch.bin_location && <span className="text-[#3A2A10]"> · {row.watch.bin_location}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-wrap justify-end max-w-full sm:max-w-[260px]">
        {Object.entries(row.status_counts).map(([label, count]) => (
          <StatusPillLink key={label} label={label} count={count} watchId={row.watch.id} />
        ))}
      </div>
      <div className="text-right min-w-[90px] sm:min-w-[120px]">
        <div className="font-mono-data text-sm text-[#0A0604]">
          {row.open_parts_count} open {row.open_parts_count === 1 ? "part" : "parts"}
        </div>
        <div className="text-[10px] text-[#3A2A10] font-mono-data">
          {row.open_spend ? fmtMoney(row.open_spend) : "—"}
        </div>
      </div>
      <div className="hidden sm:block text-right min-w-[110px]">
        <div className="text-[10px] uppercase tracking-wider text-[#3A2A10]">Services</div>
        <div className="font-mono-data text-sm text-[#0A0604]">{row.services_count}</div>
        {row.last_service_date && (
          <div className="text-[10px] text-[#3A2A10] font-mono-data">last {row.last_service_date}</div>
        )}
      </div>
      <ArrowUpRight size={13} className="text-[#3A2A10] group-hover:text-[#3A2A10] transition-colors" />
    </div>
  );
}

function StatusPillLink({ label, count, watchId }) {
  const tone =
    label === "Needed" ? "text-amber-400 border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10"
    : label === "Ordered" ? "text-sky-400 border-sky-500/30 bg-sky-500/5 hover:bg-sky-500/10"
    : label === "Shipped" ? "text-violet-400 border-violet-500/30 bg-violet-500/5 hover:bg-violet-500/10"
    : label === "Backorder" ? "text-rose-400 border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/10"
    : "text-[#3A2A10] border-[#8B6A1F]/50 bg-[#2B2214]/[0.1] hover:bg-[#2B2214]/[0.14]";
  return (
    <Link
      to={`/parts?watch_id=${watchId}&status=${encodeURIComponent(label)}`}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm transition-colors ${tone}`}
      data-testid={`attention-pill-${watchId}-${label}`}
    >
      <span>{label}</span>
      <span className="font-mono-data">{count}</span>
    </Link>
  );
}

function AttentionPanel({ attention }) {
  const items = attention?.items || [];
  const total = attention?.total || 0;
  const totalOpenSpend = attention?.total_open_spend ?? items.reduce((s, x) => s + (x.open_spend || 0), 0);
  const totalOpenParts = attention?.total_open_parts ?? items.reduce((s, x) => s + (x.open_parts_count || 0), 0);

  return (
    <section className="mb-14" data-testid="attention-panel">
      <div className="flex items-end justify-between mb-5">
        <div className="flex items-baseline gap-3">
          <h2 className="font-serif-display text-2xl text-[#0A0604]">Watches Needing Attention</h2>
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">
            {total === 0 ? "All clear" : `${total} ${total === 1 ? "piece" : "pieces"} · ${totalOpenParts} open parts · ${fmtMoney(totalOpenSpend)} outstanding`}
          </span>
        </div>
        <Link to="/workshop" className="group flex items-center gap-2 text-xs uppercase tracking-wider text-[#3A2A10] hover:text-[#E5C158]" data-testid="attention-goto-parts">
          Open Workshop
          <ArrowUpRight size={12} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </Link>
      </div>

      <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35">
        {items.length === 0 ? (
          <div className="py-14 text-center" data-testid="attention-empty">
            <Wrench size={22} className="mx-auto text-[#0A0604] mb-3" strokeWidth={1.3} />
            <div className="font-serif-display text-xl text-[#0A0604] mb-1">Bench is tidy</div>
            <div className="text-xs text-[#3A2A10] uppercase tracking-[0.2em]">No watches with open parts</div>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {items.slice(0, 8).map((row) => (
              <AttentionRow key={row.watch.id} row={row} />
            ))}
            {items.length > 8 && (
              <Link
                to="/workshop"
                className="flex items-center justify-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#3A2A10] hover:text-[#3A2A10] transition-colors"
                data-testid="attention-see-all"
              >
                <AlertCircle size={12} />
                See all {total} watches
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function ListingsPanel({ listings }) {
  const items = listings?.items || [];
  const total = listings?.total || 0;
  const totalAsking = listings?.total_asking || 0;
  const staleCount = items.filter((w) => isStaleListing(w.listed_on)).length;
  const byChannel = listings?.by_channel || [];

  return (
    <section className="mb-14" data-testid="listings-panel">
      <div className="flex items-end justify-between mb-5">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="font-serif-display text-2xl text-[#0A0604]">Currently For Sale</h2>
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">
            {total === 0
              ? "Nothing listed"
              : `${total} ${total === 1 ? "listing" : "listings"}${staleCount ? ` · ${staleCount} stale` : ""}${totalAsking ? ` · ${fmtMoney(totalAsking)} on market` : ""}`}
          </span>
        </div>
        {byChannel.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {byChannel.map((c) => (
              <span key={c.label} className="text-[10px] uppercase tracking-wider text-[#3A2A10] border border-[#8B6A1F]/45 px-1.5 py-0.5 rounded-sm">
                {c.label} <span className="font-mono-data text-[#0A0604]">{c.count}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35">
        {items.length === 0 ? (
          <div className="py-14 text-center" data-testid="listings-empty">
            <Tag size={22} className="mx-auto text-[#0A0604] mb-3" strokeWidth={1.3} />
            <div className="font-serif-display text-xl text-[#0A0604] mb-1">Nothing on the market</div>
            <div className="text-xs text-[#3A2A10] uppercase tracking-[0.2em]">Set a listing channel on a watch to surface it here</div>
          </div>
        ) : (
          <div className="divide-y divide-[#8B6A1F]/15">
            {items.slice(0, 8).map((w) => <ListingRow key={w.id} w={w} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function ListingRow({ w }) {
  const nav = useNavigate();
  const stale = isStaleListing(w.listed_on);
  const age = listingAgeLabel(w.listed_on);
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => nav(`/inventory/${w.id}`)}
      onKeyDown={(e) => { if (e.key === "Enter") nav(`/inventory/${w.id}`); }}
      className="flex flex-wrap items-center gap-3 sm:gap-5 px-4 sm:px-5 py-3.5 hover:bg-[#2B2214]/[0.06] transition-colors group cursor-pointer"
      data-testid={`listing-row-${w.id}`}
    >
      <span className="font-mono-data text-[#3A2A10] text-sm min-w-[48px]">#{w.excel_id ?? "—"}</span>
      <div className="min-w-0 flex-1 basis-[60%] sm:basis-auto">
        <div className="text-sm text-[#0A0604] truncate">
          {w.brand || "Unbranded"}
          <span className="text-[#3A2A10]"> · </span>
          <span className="font-mono-data text-[#3A2A10]">{w.caliber || "—"}</span>
        </div>
        <div className="text-[11px] text-[#3A2A10] mt-0.5 truncate">
          {w.model || "—"}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        {w.listing_channel && (
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${stale ? "bg-rose-500/10 text-rose-500 border-rose-500/30" : "bg-[#D4AF37]/10 text-[#3A2A10] border-[#D4AF37]/30"}`}>
            <Tag size={10} /> {w.listing_channel}
          </span>
        )}
        {age && (
          <span className={`text-[10px] font-mono-data ${stale ? "text-rose-500" : "text-[#3A2A10]"}`}>{age}</span>
        )}
      </div>
      <div className="text-right min-w-[90px] sm:min-w-[110px]">
        <div className="text-[10px] uppercase tracking-wider text-[#3A2A10]">Listed</div>
        <div className="font-mono-data text-sm text-[#0A0604]">
          {w.listing_price ? fmtMoney(w.listing_price) : w.sale_price ? fmtMoney(w.sale_price) : w.min_sale_price ? `${fmtMoney(w.min_sale_price)} min` : "—"}
        </div>
      </div>
      <ArrowUpRight size={13} className="text-[#3A2A10] transition-colors" />
    </div>
  );
}

/* ---------- Analytics ---------- */

/**
 * Collection performance at a glance. Four compact sub-panels:
 *   1. Headline stats  — sold count, avg time-to-sale, fastest/slowest
 *   2. ROI by Brand    — horizontal bars showing avg ROI% per brand (top 12)
 *   3. Acquisition Trend — avg all-in cost per purchase year
 *   4. Profit Histogram — 10-bucket distribution of per-watch P/L
 *
 * All data comes from GET /api/watches/analytics and excludes archived watches.
 */
function AnalyticsPanel({ analytics }) {
  const nav = useNavigate();
  if (!analytics) return null;
  const { sold_count, roi_by_brand, roi_by_caliber, margin_by_price_band, time_to_sale, acquisition_by_year, sale_by_year = [], profit_histogram } = analytics;
  if (!sold_count) {
    return (
      <section className="border-t border-[#8B6A1F]/35 pt-10 mb-14" data-testid="dashboard-analytics">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-display text-2xl text-[#0A0604] flex items-center gap-2">
            <LineChart size={16} className="text-[#3A2A10]" strokeWidth={1.5} />
            Collection Analytics
          </h2>
        </div>
        <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 px-6 py-12 text-center">
          <div className="font-serif-display text-lg text-[#0A0604] mb-1">No sold watches yet</div>
          <div className="text-xs text-[#3A2A10]">
            Analytics unlock as you close sales — ROI by brand, time-to-sale, and profit distribution will populate here.
          </div>
        </div>
      </section>
    );
  }

  const maxBrandRoi = Math.max(...roi_by_brand.map((r) => Math.abs(r.avg_roi_pct || 0)), 1);
  const maxCalRoi = Math.max(...(roi_by_caliber || []).map((r) => Math.abs(r.avg_roi_pct || 0)), 1);
  const maxBandRoi = Math.max(...(margin_by_price_band || []).map((r) => Math.abs(r.avg_roi_pct || 0)), 1);
  const maxYearCost = Math.max(...acquisition_by_year.map((a) => a.avg_all_in), 1);
  const maxYearSale = Math.max(...sale_by_year.map((s) => s.avg_sale_price), 1);
  // Joint scale + merged year list for the overlaid Cost vs Sale chart.
  // Years are unioned (some years may have purchases but no sales yet, or
  // vice-versa) so the chart shows every year in chronological order with
  // a 0-height bar standing in for any missing series.
  const yearBars = (() => {
    const acqByYr = Object.fromEntries(acquisition_by_year.map((a) => [a.year, a]));
    const saleByYr = Object.fromEntries(sale_by_year.map((s) => [s.year, s]));
    const allYears = Array.from(new Set([
      ...acquisition_by_year.map((a) => a.year),
      ...sale_by_year.map((s) => s.year),
    ])).sort();
    return allYears.map((y) => ({
      year: y,
      cost: acqByYr[y]?.avg_all_in || 0,
      cost_n: acqByYr[y]?.count || 0,
      sale: saleByYr[y]?.avg_sale_price || 0,
      sale_n: saleByYr[y]?.count || 0,
    }));
  })();
  const maxYearJoint = Math.max(maxYearCost, maxYearSale, 1);
  const maxHist = Math.max(...profit_histogram.map((b) => b.count), 1);

  return (
    <section className="border-t border-[#8B6A1F]/35 pt-10 mb-14" data-testid="dashboard-analytics">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <h2 className="font-serif-display text-2xl text-[#0A0604] flex items-center gap-2">
            <LineChart size={16} className="text-[#3A2A10]" strokeWidth={1.5} />
            Collection Analytics
          </h2>
          <div className="text-xs text-[#3A2A10] mt-1">
            Based on {sold_count} sold watches · archived excluded
          </div>
        </div>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="analytics-headline">
        <HeadlineStat
          label="Avg Time to Sale"
          value={time_to_sale.avg_days != null ? `${time_to_sale.avg_days}d` : "—"}
          sub={time_to_sale.median_days != null ? `Median ${time_to_sale.median_days}d` : null}
          icon={Clock}
          testid="analytics-avg-time"
        />
        <HeadlineStat
          label="Fastest Sale"
          value={time_to_sale.fastest != null ? `${time_to_sale.fastest}d` : "—"}
          sub="From purchase to close"
          icon={TrendingUp}
          testid="analytics-fastest"
        />
        <HeadlineStat
          label="75th Percentile"
          value={time_to_sale.p75_days != null ? `${time_to_sale.p75_days}d` : "—"}
          sub="3 in 4 sell faster"
          icon={Clock}
          testid="analytics-p75"
        />
        <HeadlineStat
          label="Sold Watches"
          value={sold_count}
          sub={roi_by_brand.length ? `${roi_by_brand.length} brand groupings` : null}
          icon={PiggyBank}
          testid="analytics-sold-count"
          hint="Counts only SOLD watches with a non-zero sale_price (ROI needs one). The Total Watches stat above may be higher if some SOLD entries haven't had a price recorded yet."
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ROI by Brand — click a bar to filter Inventory to that brand's sold watches */}
        <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5" data-testid="analytics-roi-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#3A2A10]">ROI by Brand</h3>
            <span className="text-[9px] uppercase tracking-wider text-[#3A2A10]/70">click to drill in</span>
          </div>
          {roi_by_brand.length === 0 ? (
            <div className="text-xs text-[#3A2A10] py-6 text-center">Need 2+ sales per brand to compute ROI</div>
          ) : (
            <div className="space-y-1">
              {roi_by_brand.map((r) => (
                <RoiRow
                  key={r.brand}
                  row={r}
                  max={maxBrandRoi}
                  labelKey="brand"
                  onClick={(row) => nav(`/inventory?brand=${encodeURIComponent(row.brand)}&status=SOLD&hideSold=0`)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ROI by Caliber — same treatment, filter on caliber */}
        <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5" data-testid="analytics-roi-caliber-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#3A2A10]">ROI by Caliber</h3>
            <span className="text-[9px] uppercase tracking-wider text-[#3A2A10]/70">click to drill in</span>
          </div>
          {!roi_by_caliber || roi_by_caliber.length === 0 ? (
            <div className="text-xs text-[#3A2A10] py-6 text-center">Need 2+ sales per caliber to compute ROI</div>
          ) : (
            <div className="space-y-1">
              {roi_by_caliber.map((r) => (
                <RoiRow
                  key={r.caliber}
                  row={r}
                  max={maxCalRoi}
                  labelKey="caliber"
                  onClick={(row) => nav(`/inventory?caliber=${encodeURIComponent(row.caliber)}&status=SOLD&hideSold=0`)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Margin by Price Band — does paying more yield better margins? */}
        <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5 lg:col-span-2" data-testid="analytics-price-band-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#3A2A10]">Margin by Acquisition Price</h3>
            <span className="text-[9px] uppercase tracking-wider text-[#3A2A10]/70">does paying more pay off?</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            {margin_by_price_band.map((b) => (
              <PriceBandCard key={b.label} band={b} max={maxBandRoi} />
            ))}
          </div>
        </div>

        {/* Profit Histogram */}
        <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5" data-testid="analytics-profit-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#3A2A10]">Profit Distribution</h3>
            <span className="text-[9px] uppercase tracking-wider text-[#3A2A10]/70">per watch</span>
          </div>
          {profit_histogram.length === 0 ? (
            <div className="text-xs text-[#3A2A10] py-6 text-center">Need sold watches with profit data</div>
          ) : (
            <div className="flex items-end gap-1 h-32" data-testid="analytics-histogram">
              {profit_histogram.map((b, i) => {
                const h = (b.count / maxHist) * 100;
                const positive = b.range_hi > 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 group" title={`${fmtMoney(b.range_lo)} – ${fmtMoney(b.range_hi)} · ${b.count} watches`}>
                    <div
                      className={`w-full transition-all ${positive ? "bg-emerald-600/60 group-hover:bg-emerald-600" : "bg-rose-500/60 group-hover:bg-rose-500"}`}
                      style={{ height: `${h}%`, minHeight: b.count ? "2px" : "0" }}
                    />
                  </div>
                );
              })}
            </div>
          )}
          {profit_histogram.length > 0 && (
            <div className="flex justify-between mt-2 text-[9px] font-mono-data text-[#3A2A10]">
              <span>{fmtMoney(profit_histogram[0].range_lo)}</span>
              <span>{fmtMoney(profit_histogram[profit_histogram.length - 1].range_hi)}</span>
            </div>
          )}
        </div>

        {/* Cost vs Sale by Year — overlaid bars so the margin gap is visible at a glance */}
        <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5 lg:col-span-2" data-testid="analytics-cost-vs-sale-panel">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#3A2A10]">Cost vs Sale by Year</h3>
            <div className="flex items-center gap-4 text-[10px] text-[#3A2A10]">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 bg-[#D4AF37]/70" /> Acquisition (purch year)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 bg-emerald-700/70" /> Sale (sale year)
              </span>
            </div>
          </div>
          <div className="text-[10px] text-[#3A2A10]/80 mb-4">
            Gap between gold and emerald = average margin per piece that year.
          </div>
          {yearBars.length === 0 ? (
            <div className="text-xs text-[#3A2A10] py-6 text-center">
              Fill in purchase + sale dates to see this comparison
            </div>
          ) : (
            <div className="flex items-stretch gap-4" data-testid="analytics-cost-vs-sale-bars">
              {yearBars.map((b) => {
                const hCost = (b.cost / maxYearJoint) * 100;
                const hSale = (b.sale / maxYearJoint) * 100;
                const margin = b.cost && b.sale ? b.sale - b.cost : null;
                return (
                  <div key={b.year} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
                    <div className="text-[9px] font-mono-data text-[#3A2A10] leading-tight text-center h-6">
                      <span className="text-[#8B6A1F]">{b.cost ? fmtMoney(b.cost) : "—"}</span>
                      <span className="opacity-50"> / </span>
                      <span className="text-emerald-700">{b.sale ? fmtMoney(b.sale) : "—"}</span>
                    </div>
                    {/* Bars row: fixed height container so percent heights resolve.
                        Two columns side-by-side with items-end so they grow up
                        from a shared baseline. */}
                    <div className="flex items-end justify-center gap-px w-full h-36">
                      <div
                        className="bg-[#D4AF37]/70 hover:bg-[#D4AF37] transition-colors flex-1 max-w-[50%]"
                        style={{ height: `${hCost}%`, minHeight: b.cost ? "3px" : "0" }}
                        title={b.cost
                          ? `${b.cost_n} watch${b.cost_n === 1 ? "" : "es"} bought in ${b.year}, avg ${fmtMoney(b.cost)}`
                          : `No purchases in ${b.year}`}
                      />
                      <div
                        className="bg-emerald-700/70 hover:bg-emerald-700 transition-colors flex-1 max-w-[50%]"
                        style={{ height: `${hSale}%`, minHeight: b.sale ? "3px" : "0" }}
                        title={b.sale
                          ? `${b.sale_n} watch${b.sale_n === 1 ? "" : "es"} sold in ${b.year}, avg ${fmtMoney(b.sale)}`
                          : `No sales in ${b.year}`}
                      />
                    </div>
                    <div className="text-[10px] font-mono-data text-[#0A0604]">{b.year}</div>
                    <div className="text-[9px] text-[#3A2A10] leading-tight text-center">
                      <span title="bought / sold">
                        {b.cost_n || 0}b · {b.sale_n || 0}s
                      </span>
                      {margin !== null && (
                        <div className={`mt-0.5 font-mono-data ${margin >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                          {margin >= 0 ? "+" : ""}{fmtMoney(margin)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function HeadlineStat({ label, value, sub, icon: Icon, testid, hint }) {
  return (
    <div
      className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-4"
      data-testid={testid}
      title={hint}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] uppercase tracking-[0.25em] text-[#3A2A10]">{label}</span>
        <Icon size={12} className="text-[#3A2A10]" />
      </div>
      <div className="font-mono-data text-2xl text-[#0A0604] leading-none">{value}</div>
      {sub && <div className="text-[10px] text-[#3A2A10] mt-1.5">{sub}</div>}
    </div>
  );
}

/**
 * A single acquisition-price-band card: avg ROI% as a coloured bar (emerald
 * positive, rose negative), count + avg profit $ as secondary detail. Gives
 * an at-a-glance read on "am I making more on cheap watches or expensive
 * ones?" — the core sourcing question.
 */
function PriceBandCard({ band, max }) {
  const roi = band.avg_roi_pct;
  const positive = roi != null && roi >= 0;
  const pct = roi != null ? Math.min(100, (Math.abs(roi) / max) * 100) : 0;
  const hasData = band.count > 0;
  return (
    <div
      className={`border ${hasData ? "border-[#8B6A1F]/35" : "border-[#8B6A1F]/20"} bg-[#FBF3D9] px-3 py-3 ${hasData ? "" : "opacity-50"}`}
      data-testid={`analytics-price-band-${band.label}`}
      title={hasData ? `${band.count} sold · avg $-profit ${fmtMoney(band.avg_profit)} · avg all-in ${fmtMoney(band.avg_all_in)}` : "No sold watches in this band"}
    >
      <div className="text-[10px] uppercase tracking-wider text-[#3A2A10] mb-2">{band.label}</div>
      <div className={`font-mono-data text-xl leading-none ${positive ? "text-emerald-700" : roi != null ? "text-rose-600" : "text-[#3A2A10]"}`}>
        {roi != null ? `${roi > 0 ? "+" : ""}${roi.toFixed(1)}%` : "—"}
      </div>
      <div className="mt-2 h-[3px] bg-[#2B2214]/[0.14] relative">
        <div
          className={`absolute left-0 top-0 h-full ${positive ? "bg-emerald-600/70" : "bg-rose-500/70"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] font-mono-data text-[#3A2A10]">
        <span>n={band.count}</span>
        <span>{hasData ? `avg ${fmtMoney(band.avg_profit)}` : "—"}</span>
      </div>
    </div>
  );
}

function RoiRow({ row, max, labelKey = "brand", onClick }) {
  const roi = row.avg_roi_pct;
  const pct = roi != null ? Math.min(100, (Math.abs(roi) / max) * 100) : 0;
  const positive = roi != null && roi >= 0;
  const label = row[labelKey];
  const clickable = !!onClick && label && label !== "Other";
  return (
    <div
      className={`flex items-center gap-3 py-1 ${clickable ? "cursor-pointer hover:bg-[#2B2214]/[0.06] -mx-2 px-2 transition-colors" : ""}`}
      onClick={() => clickable && onClick(row)}
      data-testid={`analytics-roi-${labelKey}-${label}`}
      title={clickable ? `Click to see the ${row.count} sold ${label} watches` : undefined}
    >
      <div className="w-28 text-sm text-[#0A0604] truncate">{label}</div>
      <div className="flex-1 h-[3px] bg-[#2B2214]/[0.14] relative">
        <div
          className={`absolute left-0 top-0 h-full ${positive ? "bg-emerald-600/70" : "bg-rose-500/70"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className={`font-mono-data text-xs w-16 text-right ${positive ? "text-emerald-700" : "text-rose-600"}`}>
        {roi != null ? `${roi > 0 ? "+" : ""}${roi.toFixed(1)}%` : "—"}
      </div>
      <div className="font-mono-data text-[10px] text-[#3A2A10] w-10 text-right">n={row.count}</div>
    </div>
  );
}
